<template>
  <a-modal
    :visible="visible"
    :width="640"
    title="Filtrar e ordenar"
    wrap-class-name="freight-tables-filter-modal"
    @cancel="$emit('close')"
  >
    <div class="freight-tables-filter-modal__section">
      <h4 class="freight-tables-filter-modal__label">Ordenar por</h4>

      <a-radio-group v-model="draft.ordenar">
        <a-radio value="az">A-Z</a-radio>
        <a-radio value="za">Z-A</a-radio>
      </a-radio-group>
    </div>

    <div
      class="freight-tables-filter-modal__section freight-tables-filter-modal__statuses"
    >
      <div class="freight-tables-filter-modal__field">
        <h4 class="freight-tables-filter-modal__label">Status da tabela</h4>

        <a-select
          v-model="draft.tableStatus"
          class="freight-tables-filter-modal__select"
        >
          <a-select-option
            v-for="option in tableStatusOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </div>

      <div class="freight-tables-filter-modal__field">
        <h4 class="freight-tables-filter-modal__label">Status da vigência</h4>

        <a-select
          v-model="draft.effectivePeriodStatus"
          class="freight-tables-filter-modal__select"
        >
          <a-select-option
            v-for="option in validityStatusOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </div>
    </div>

    <div class="freight-tables-filter-modal__section">
      <h4 class="freight-tables-filter-modal__label">Fornecedor</h4>

      <a-select
        v-model="draft.fornecedor"
        mode="multiple"
        label-in-value
        placeholder="Digite o nome do fornecedor"
        class="freight-tables-filter-modal__suppliers"
        :filter-option="false"
        :loading="suppliersLoading"
        :not-found-content="notFoundContent"
        @search="onSupplierSearch"
      >
        <a-select-option
          v-for="option in supplierOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ supplierLabel(option) }}
        </a-select-option>
      </a-select>
    </div>

    <template slot="footer">
      <a-button @click="handleClear">Limpar filtro</a-button>
      <a-button type="primary" :disabled="!isDirty" @click="handleApply">
        Aplicar
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import { mapActions } from "vuex";
import debounce from "lodash/debounce";
import {
  FREIGHT_TABLE_STATUS_FILTER,
  FREIGHT_TABLE_STATUS_FILTER_OPTIONS,
} from "../../constants/freightTableStatus";
import {
  FREIGHT_VALIDITY_STATUS_FILTER,
  FREIGHT_VALIDITY_STATUS_FILTER_OPTIONS,
} from "../../constants/freightValidityStatus";
import { SUPPLIER_SEARCH_MIN_LENGTH } from "../../constants/freightTableOptions";
import { handleApiError } from "@/shared/utils/handleApiError";

const SORT_BY_OPTION = {
  az: "descr|asc",
  za: "descr|desc",
};

function buildDraft(filters) {
  const source = filters || {};
  let ordenar = null;
  if (source.sort === SORT_BY_OPTION.az) ordenar = "az";
  if (source.sort === SORT_BY_OPTION.za) ordenar = "za";

  return {
    ordenar,
    tableStatus: source.tableStatus || FREIGHT_TABLE_STATUS_FILTER.ALL,
    effectivePeriodStatus:
      source.effectivePeriodStatus || FREIGHT_VALIDITY_STATUS_FILTER.ALL,
    fornecedor: (source.fornecedor || []).slice(),
  };
}

function supplierKeys(fornecedor) {
  return (fornecedor || [])
    .map((item) => Number(item.key))
    .sort((a, b) => a - b)
    .join(",");
}

export default {
  name: "FreightTablesFilterModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      tableStatusOptions: FREIGHT_TABLE_STATUS_FILTER_OPTIONS,
      validityStatusOptions: FREIGHT_VALIDITY_STATUS_FILTER_OPTIONS,
      draft: buildDraft(this.filters),
      supplierOptions: [],
      suppliersLoading: false,
      supplierTerm: "",
    };
  },
  computed: {
    isDirty() {
      const applied = buildDraft(this.filters);
      return (
        this.draft.ordenar !== applied.ordenar ||
        this.draft.tableStatus !== applied.tableStatus ||
        this.draft.effectivePeriodStatus !== applied.effectivePeriodStatus ||
        supplierKeys(this.draft.fornecedor) !== supplierKeys(applied.fornecedor)
      );
    },
    notFoundContent() {
      if (this.suppliersLoading) return "Buscando...";
      if (this.supplierTerm.length < SUPPLIER_SEARCH_MIN_LENGTH) {
        return `Digite ao menos ${SUPPLIER_SEARCH_MIN_LENGTH} caracteres`;
      }
      return "Nenhum fornecedor encontrado";
    },
  },
  created() {
    this.debouncedSupplierSearch = debounce(this.loadSuppliers, 500);
  },
  beforeDestroy() {
    this.debouncedSupplierSearch.cancel();
  },
  watch: {
    visible(isVisible) {
      if (!isVisible) return;
      this.draft = buildDraft(this.filters);
      this.supplierOptions = [];
      this.supplierTerm = "";
    },
  },
  methods: {
    ...mapActions("freightTables", ["searchSuppliers"]),
    supplierLabel(option) {
      return option.documento
        ? `${option.label} — ${option.documento}`
        : option.label;
    },
    onSupplierSearch(term) {
      this.supplierTerm = String(term || "").trim();

      if (this.supplierTerm.length < SUPPLIER_SEARCH_MIN_LENGTH) {
        this.debouncedSupplierSearch.cancel();
        this.supplierOptions = [];
        this.suppliersLoading = false;
        return;
      }

      this.suppliersLoading = true;
      this.debouncedSupplierSearch();
    },
    async loadSuppliers() {
      const term = this.supplierTerm;

      try {
        const options = await this.searchSuppliers({ search: term });
        if (term !== this.supplierTerm) return;
        this.supplierOptions = options;
      } catch (error) {
        this.supplierOptions = [];
        handleApiError(this, error, "Não foi possível buscar fornecedores");
      } finally {
        if (term === this.supplierTerm) this.suppliersLoading = false;
      }
    },
    handleApply() {
      if (!this.isDirty) return;

      this.$emit("apply", {
        sort: this.draft.ordenar ? SORT_BY_OPTION[this.draft.ordenar] : null,
        tableStatus: this.draft.tableStatus,
        effectivePeriodStatus: this.draft.effectivePeriodStatus,
        fornecedor: this.draft.fornecedor.slice(),
      });
    },
    handleClear() {
      this.draft = buildDraft({});
      this.$emit("clear");
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-tables-filter-modal__section {
  padding: 16px 0;

  &:first-child {
    padding-top: 0;
  }

  & + & {
    border-top: 1px solid @color-bg-subtle;
  }
}

.freight-tables-filter-modal__label {
  font-size: 14px;
  font-weight: 600;
  color: @color-text-dark;
  margin: 0 0 12px 0;
}

.freight-tables-filter-modal__suppliers {
  width: 100%;
}

.freight-tables-filter-modal__statuses {
  display: flex;
  gap: 16px;
}

.freight-tables-filter-modal__field {
  flex: 1;
  min-width: 0;
}

.freight-tables-filter-modal__select {
  width: 100%;
}
</style>

<style lang="less">
@import "~@/shared/styles/variables.less";

.freight-tables-filter-modal {
  .ant-modal-title {
    font-size: 16px;
    font-weight: 600;
    color: @color-text-dark;
  }

  .ant-modal-body {
    padding: 24px;
  }

  .ant-radio-checked .ant-radio-inner {
    border-color: @color-primary;

    &::after {
      background-color: @color-primary;
    }
  }

  .ant-btn-primary:not([disabled]) {
    background-color: @color-primary;
    border-color: @color-primary;

    &:hover,
    &:focus {
      background-color: @color-primary-hover;
      border-color: @color-primary-hover;
    }
  }
}
</style>
